import type { ReaderReportType } from "@/lib/reports/report-type";
import { EXPLORE_FILTERS_SEGMENT } from "./explore-search";

/**
 * Only exact route shapes that are guaranteed to exist across editions should
 * survive an edition switch. Everything else must fall back to the hub.
 *
 * This fail-closed policy prevents detail/category URLs from being blindly
 * rewritten into the other edition and 404ing there.
 */
type ExploreEditionSwitchRouteKind =
  | "hub"
  | "storiesIndex"
  | "skillsIndex"
  | "skillCategoryDetail"
  | "toolsIndex"
  | "toolDetail"
  | "toolCategoriesIndex"
  | "toolCategoryDetail"
  | "tagsIndex"
  | "other";

function normalizeExplorePathname({ pathname }: { pathname: string }): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

function extractDetailSlug({
  pathname,
  prefix,
}: {
  pathname: string;
  prefix: string;
}): string | null {
  if (!pathname.startsWith(prefix)) {
    return null;
  }

  const detailSlug = pathname.slice(prefix.length);
  if (!detailSlug || detailSlug.includes("/")) {
    return null;
  }

  return detailSlug;
}

function classifyExploreEditionSwitchRoute({
  currentPathname,
  currentReportType,
}: {
  currentPathname: string;
  currentReportType: ReaderReportType;
}): ExploreEditionSwitchRouteKind {
  const normalizedPathname = normalizeExplorePathname({
    pathname: currentPathname,
  });
  const currentBase = buildExploreBase({ reportType: currentReportType });
  const currentPublicPrefix = buildPublicExplorePathPrefix({
    reportType: currentReportType,
  });
  const currentHub = getHubUrl({ reportType: currentReportType });
  const currentLegacyCreativeRoot =
    currentReportType === "creative" ? "/" : null;

  if (
    normalizedPathname === currentHub ||
    normalizedPathname === currentBase ||
    normalizedPathname === currentLegacyCreativeRoot
  ) {
    return "hub";
  }

  if (
    normalizedPathname === `${currentBase}/stories` ||
    normalizedPathname === `${currentPublicPrefix}/stories`
  ) {
    return "storiesIndex";
  }

  if (
    normalizedPathname === `${currentBase}/skills` ||
    normalizedPathname === `${currentPublicPrefix}/skills`
  ) {
    return "skillsIndex";
  }

  if (
    extractDetailSlug({
      pathname: normalizedPathname,
      prefix: `${currentBase}/skills/category/`,
    }) ||
    extractDetailSlug({
      pathname: normalizedPathname,
      prefix: `${currentPublicPrefix}/skills/category/`,
    })
  ) {
    return "skillCategoryDetail";
  }

  if (
    normalizedPathname === `${currentBase}/tools` ||
    normalizedPathname === `${currentPublicPrefix}/tools`
  ) {
    return "toolsIndex";
  }

  if (
    normalizedPathname === `${currentBase}/tools/categories` ||
    normalizedPathname === `${currentPublicPrefix}/tools/categories`
  ) {
    return "toolCategoriesIndex";
  }

  if (
    extractDetailSlug({
      pathname: normalizedPathname,
      prefix: `${currentBase}/tools/categories/`,
    }) ||
    extractDetailSlug({
      pathname: normalizedPathname,
      prefix: `${currentPublicPrefix}/tools/categories/`,
    })
  ) {
    return "toolCategoryDetail";
  }

  if (
    extractDetailSlug({
      pathname: normalizedPathname,
      prefix: `${currentBase}/tools/`,
    }) ||
    extractDetailSlug({
      pathname: normalizedPathname,
      prefix: `${currentPublicPrefix}/tools/`,
    })
  ) {
    return "toolDetail";
  }

  if (
    normalizedPathname === `${currentBase}/tags` ||
    normalizedPathname === `${currentPublicPrefix}/tags`
  ) {
    return "tagsIndex";
  }

  return "other";
}

/**
 * Parse reportType from a client-side pathname.
 *
 * `usePathname()` returns the pre-rewrite path, so short URLs like
 * `/engineer` appear as-is rather than `/en/engineer/explore`.
 */
export function parseReportTypeFromPathname({
  pathname,
}: {
  pathname: string;
}): ReaderReportType {
  if (
    pathname === "/" ||
    pathname === "/engineer" ||
    pathname.includes("/engineer/")
  ) {
    return "engineer";
  }
  return "creative";
}

/**
 * Build the internal explore base path for a given reportType.
 * Example: `"engineer"` → `"/en/engineer/explore"`
 */
export function buildExploreBase({
  reportType,
}: {
  reportType: ReaderReportType;
}): string {
  return `/en/${reportType}/explore`;
}

export function buildPublicExplorePathPrefix({
  reportType,
}: {
  reportType: ReaderReportType;
}): string {
  return `/${reportType}`;
}

export function buildShortExploreUrl({
  reportType,
  suffix = "",
}: {
  reportType: ReaderReportType;
  suffix?: string;
}): string {
  return `${buildPublicExplorePathPrefix({ reportType })}${suffix}`;
}

/**
 * Get the public hub URL for a given reportType.
 * Example: `"engineer"` → `"/engineer"`, `"creative"` → `"/creative"`
 */
export function getHubUrl({
  reportType,
}: {
  reportType: ReaderReportType;
}): string {
  // The engineer hub is served at "/" (the root URL) to align with
  // Google's canonical selection and avoid duplicate-content issues.
  if (reportType === "engineer") {
    return "/";
  }
  return buildShortExploreUrl({ reportType });
}

const LEGACY_CATEGORY_REDIRECTS: Record<
  ReaderReportType,
  Record<string, string>
> = {
  engineer: {
    "agentic-engineering": "coding-tools",
    "agent-development": "agent-platforms",
    "models-and-apis": "models-serving",
    "voice-agents": "agent-platforms",
    "inference-and-infrastructure": "models-serving",
    "security-and-reliability": "trust-evaluation-reliability",
    "cost-and-operations": "pricing-limits-cost",
    "research-and-benchmarks": "trust-evaluation-reliability",
    "business-and-policy": "adoption-market-strategy",
  },
  creative: {
    "video-and-film": "video-motion-3d",
    "image-and-design": "image-visual-design",
    "audio-and-voice": "brand-audio-marketing",
    "product-design": "product-interface-design",
    "vibe-coding": "creative-coding-websites",
    writing: "creator-workflows-pipelines",
  },
} satisfies Record<ReaderReportType, Record<string, string>>;

export function getLegacyCategoryRedirectTarget({
  reportType,
  categorySlug,
}: {
  reportType: ReaderReportType;
  categorySlug: string;
}): string | null {
  return LEGACY_CATEGORY_REDIRECTS[reportType][categorySlug] ?? null;
}

export type CategoryRouteTransition =
  | { kind: "render"; categorySlug: string }
  | { kind: "redirect"; categorySlug: string }
  | { kind: "notFound" };

export function resolveCategoryRouteTransition({
  reportType,
  categorySlug,
  liveCategorySlugs,
}: {
  reportType: ReaderReportType;
  categorySlug: string;
  liveCategorySlugs: readonly string[];
}): CategoryRouteTransition {
  const liveCategorySlugSet = new Set(liveCategorySlugs);
  const redirectTarget = getLegacyCategoryRedirectTarget({
    reportType,
    categorySlug,
  });

  if (redirectTarget && liveCategorySlugSet.has(redirectTarget)) {
    return { kind: "redirect", categorySlug: redirectTarget };
  }

  if (liveCategorySlugSet.has(categorySlug)) {
    return { kind: "render", categorySlug };
  }

  return { kind: "notFound" };
}

export function buildPublicCategoryRoutePath({
  reportType,
  categorySlug,
  filterSegments,
}: {
  reportType: ReaderReportType;
  categorySlug: string;
  filterSegments?: { tagSlugs: string; layout: string };
}): string {
  const basePath = `${buildPublicExplorePathPrefix({ reportType })}/${categorySlug}`;

  if (!filterSegments) {
    return basePath;
  }

  return `${basePath}/${EXPLORE_FILTERS_SEGMENT}/${filterSegments.tagSlugs}/${filterSegments.layout}`;
}

/**
 * Compute the target URL when switching between editions.
 *
 * Only routes that are explicitly known to be safe across editions are
 * preserved. Story detail pages, tag detail pages, categories, hidden pages,
 * and any future nested sub-routes drop back to the target hub unless they are
 * explicitly added to the shared route registry.
 */
export function switchExploreEdition({
  currentPathname,
  currentReportType,
  targetReportType,
}: {
  currentPathname: string;
  currentReportType: ReaderReportType;
  targetReportType: ReaderReportType;
}): string {
  const routeKind = classifyExploreEditionSwitchRoute({
    currentPathname,
    currentReportType,
  });
  const normalizedPathname = normalizeExplorePathname({
    pathname: currentPathname,
  });
  const targetPublicPrefix = buildPublicExplorePathPrefix({
    reportType: targetReportType,
  });
  const targetHub = getHubUrl({ reportType: targetReportType });

  if (routeKind === "storiesIndex") {
    return `${targetPublicPrefix}/stories`;
  }

  if (routeKind === "skillsIndex") {
    return `${targetPublicPrefix}/skills`;
  }

  if (routeKind === "skillCategoryDetail") {
    const categorySlug =
      extractDetailSlug({
        pathname: normalizedPathname,
        prefix: `${buildExploreBase({ reportType: currentReportType })}/skills/category/`,
      }) ??
      extractDetailSlug({
        pathname: normalizedPathname,
        prefix: `${buildPublicExplorePathPrefix({ reportType: currentReportType })}/skills/category/`,
      });

    return categorySlug
      ? `${targetPublicPrefix}/skills/category/${categorySlug}`
      : targetHub;
  }

  if (routeKind === "toolsIndex") {
    return `${targetPublicPrefix}/tools`;
  }

  if (routeKind === "toolCategoriesIndex") {
    return `${targetPublicPrefix}/tools/categories`;
  }

  if (routeKind === "toolCategoryDetail") {
    const categorySlug =
      extractDetailSlug({
        pathname: normalizedPathname,
        prefix: `${buildExploreBase({ reportType: currentReportType })}/tools/categories/`,
      }) ??
      extractDetailSlug({
        pathname: normalizedPathname,
        prefix: `${buildPublicExplorePathPrefix({ reportType: currentReportType })}/tools/categories/`,
      });

    return categorySlug
      ? `${targetPublicPrefix}/tools/categories/${categorySlug}`
      : targetHub;
  }

  if (routeKind === "toolDetail") {
    const detailSlug =
      extractDetailSlug({
        pathname: normalizedPathname,
        prefix: `${buildExploreBase({ reportType: currentReportType })}/tools/`,
      }) ??
      extractDetailSlug({
        pathname: normalizedPathname,
        prefix: `${buildPublicExplorePathPrefix({ reportType: currentReportType })}/tools/`,
      });

    return detailSlug ? `${targetPublicPrefix}/tools/${detailSlug}` : targetHub;
  }

  if (routeKind === "tagsIndex") {
    return `${targetPublicPrefix}/tags`;
  }

  return targetHub;
}
